// app/features/activation/hooks/useActivationNewsletterTrigger.ts
'use client';

import { useEffect, useRef } from 'react';

import { useAuthStore } from '@/app/auth/services_and_hooks/authStore';
import { activationService } from '@/app/shared/services_and_hooks/activationService';

/**
 * Hook that automatically checks and triggers the welcome newsletter
 * when a user completes activation (creates org + subscribes to newsletter)
 * 
 * This runs once per session and uses the cached flag for performance.
 * 
 * Best Practices:
 * - Checks cached flag first (no DB query unless needed)
 * - Only triggers once per session
 * - Idempotent (backend has additional checks)
 * - Non-blocking (doesn't affect UI rendering)
 */
export function useActivationNewsletterTrigger() {
  const user = useAuthStore((state) => state.user);
  const hasCompletedOnboarding = useAuthStore((state) => state.hasCompletedOnboarding);
  
  // Track if we've already checked in this session to avoid duplicate calls
  const hasCheckedThisSession = useRef(false);

  useEffect(() => {
    // 🔍 DEBUG: Log all conditions
    console.log('[useActivationNewsletterTrigger] Checking conditions:', {
      hasUser: !!user,
      hasCheckedThisSession: hasCheckedThisSession.current,
      hasCompletedOnboarding
    });

    // Skip if:
    // - No user logged in
    // - Already checked this session
    // - Onboarding not complete
    if (!user || hasCheckedThisSession.current || !hasCompletedOnboarding) {
      console.log('[useActivationNewsletterTrigger] Skipping check - conditions not met');
      return;
    }

    // Mark as checked for this session
    hasCheckedThisSession.current = true;

    console.log('[useActivationNewsletterTrigger] ✅ All conditions passed! Checking if user qualifies for welcome newsletter...');

    // Trigger activation check (non-blocking)
    // Backend will check if user already has personalization results
    activationService
      .triggerActivationComplete()
      .then((result) => {
        console.log('[useActivationNewsletterTrigger] Activation check result:', result);
      })
      .catch((error) => {
        console.error('[useActivationNewsletterTrigger] Error checking activation:', error);
        // Will retry next session
      });
  }, [user, hasCompletedOnboarding]);
}

